// src/lib/emailService.js

// Cloudflare Worker URL - your email proxy
const EMAIL_PROXY_URL = 'https://email-proxy.raola86.workers.dev'

// ============== BASE SEND FUNCTION ==============
export const sendEmail = async ({ to, subject, html, fromName, fromEmail = 'notifications@estatesflow.online' }) => {
  try {
    const from = `${fromName} <${fromEmail}>`
    
    const response = await fetch(EMAIL_PROXY_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        from: from,
        to: [to],
        subject: subject,
        html: html
      })
    })
    
    const data = await response.json()
    
    if (data.error || data.statusCode >= 400) {
      console.error('Resend error:', data)
      return { success: false, error: data.message || data.error }
    }
    
    console.log('Email sent successfully:', { to, subject })
    return { success: true, data }
  } catch (error) {
    console.error('Email send error:', error)
    return { success: false, error: error.message }
  }
}

// ============== STAFF INVITATION (Leadership Portal) ==============
export const sendStaffInvitationEmail = async (to, invitationLink, role, department, invitedBy, companyName) => {
  const html = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>Invitation to Join ${companyName}</title>
      <style>
        body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; margin: 0; padding: 0; background-color: #f4f4f4; }
        .container { max-width: 600px; margin: 0 auto; padding: 20px; }
        .card { background: #ffffff; border-radius: 8px; overflow: hidden; box-shadow: 0 2px 10px rgba(0,0,0,0.1); }
        .header { background: linear-gradient(135deg, #1e3a5f 0%, #0f2b42 100%); padding: 30px; text-align: center; }
        .header h1 { color: #ffffff; margin: 0; font-size: 24px; }
        .content { padding: 30px; }
        .info-box { background-color: #f0f4f8; padding: 20px; border-radius: 8px; margin: 20px 0; }
        .button { display: inline-block; background-color: #059669; color: #ffffff; padding: 12px 24px; text-decoration: none; border-radius: 8px; margin: 20px 0; }
        .footer { background-color: #f8fafc; padding: 20px; text-align: center; font-size: 12px; color: #64748b; }
        .company-name { color: #1e3a5f; font-weight: bold; }
      </style>
    </head>
    <body>
      <div class="container">
        <div class="card">
          <div class="header">
            <h1>🏢 You're Invited!</h1>
          </div>
          <div class="content">
            <h2 style="color: #1e3a5f; margin-top: 0;">Join <span class="company-name">${escapeHtml(companyName)}</span></h2>
            <p><strong>${escapeHtml(invitedBy)}</strong> has invited you to join their organization on EstatesFlow CRM.</p>
            <div class="info-box">
              <p style="margin: 5px 0;"><strong>👔 Role:</strong> ${escapeHtml(role)}</p>
              <p style="margin: 5px 0;"><strong>📂 Department:</strong> ${escapeHtml(department)}</p>
            </div>
            <p>Click the button below to create your account and get started:</p>
            <div style="text-align: center;">
              <a href="${invitationLink}" class="button">Accept Invitation →</a>
            </div>
            <p style="font-size: 12px; color: #64748b;">Or copy this link: ${invitationLink}</p>
            <p style="font-size: 12px; color: #64748b;">This invitation expires in 7 days.</p>
          </div>
          <div class="footer">
            <p>EstatesFlow CRM - Complete Real Estate Management</p>
            <p>📍 Jikwoyi, AMAC Abuja, Nigeria</p>
          </div>
        </div>
      </div>
    </body>
    </html>
  `

  return sendEmail({
    to,
    subject: `Invitation to Join ${companyName}`,
    html,
    fromName: companyName
  })
}

// ============== INVOICE EMAIL (Accounting Portal) ==============
export const sendInvoiceEmail = async (to, clientName, invoiceNumber, amount, currency, dueDate, companyName) => {
  const html = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>Invoice ${invoiceNumber}</title>
      <style>
        body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; margin: 0; padding: 0; background-color: #f4f4f4; }
        .container { max-width: 600px; margin: 0 auto; padding: 20px; }
        .card { background: #ffffff; border-radius: 8px; overflow: hidden; box-shadow: 0 2px 10px rgba(0,0,0,0.1); }
        .header { background: linear-gradient(135deg, #1e3a5f 0%, #0f2b42 100%); padding: 30px; text-align: center; }
        .header h1 { color: #ffffff; margin: 0; font-size: 24px; }
        .content { padding: 30px; }
        .invoice-details { background-color: #f0f4f8; padding: 20px; border-radius: 8px; margin: 20px 0; }
        .button { display: inline-block; background-color: #1e3a5f; color: #ffffff; padding: 12px 24px; text-decoration: none; border-radius: 8px; margin: 20px 0; }
        .footer { background-color: #f8fafc; padding: 20px; text-align: center; font-size: 12px; color: #64748b; }
      </style>
    </head>
    <body>
      <div class="container">
        <div class="card">
          <div class="header">
            <h1>📄 Invoice ${invoiceNumber}</h1>
          </div>
          <div class="content">
            <h2 style="color: #1e3a5f; margin-top: 0;">Hello ${escapeHtml(clientName)},</h2>
            <p>Your invoice <strong>${invoiceNumber}</strong> from <strong>${escapeHtml(companyName)}</strong> is ready for payment.</p>
            <div class="invoice-details">
              <p style="margin: 5px 0;"><strong>💰 Amount:</strong> ${currency} ${amount.toLocaleString()}</p>
              <p style="margin: 5px 0;"><strong>📅 Due Date:</strong> ${new Date(dueDate).toLocaleDateString()}</p>
            </div>
            <div style="text-align: center;">
              <a href="https://estatesflow.online/accounting" class="button">View Invoice</a>
            </div>
          </div>
          <div class="footer">
            <p>${escapeHtml(companyName)} - Powered by EstatesFlow CRM</p>
            <p>📍 Jikwoyi, AMAC Abuja, Nigeria</p>
          </div>
        </div>
      </div>
    </body>
    </html>
  `

  return sendEmail({
    to,
    subject: `Invoice ${invoiceNumber} from ${companyName}`,
    html,
    fromName: companyName
  })
}

// ============== FINANCIAL REPORT EMAIL (Accounting Portal) ==============
export const sendReportEmail = async (to, reportName, totalIncome, totalExpenses, profit, companyName) => {
  const html = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>Financial Report</title>
      <style>
        body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; margin: 0; padding: 0; background-color: #f4f4f4; }
        .container { max-width: 600px; margin: 0 auto; padding: 20px; }
        .card { background: #ffffff; border-radius: 8px; overflow: hidden; box-shadow: 0 2px 10px rgba(0,0,0,0.1); }
        .header { background: linear-gradient(135deg, #1e3a5f 0%, #0f2b42 100%); padding: 30px; text-align: center; }
        .header h1 { color: #ffffff; margin: 0; font-size: 24px; }
        .content { padding: 30px; }
        .report-details { background-color: #f0f4f8; padding: 20px; border-radius: 8px; margin: 20px 0; }
        .profit { color: #059669; }
        .loss { color: #dc2626; }
        .button { display: inline-block; background-color: #1e3a5f; color: #ffffff; padding: 12px 24px; text-decoration: none; border-radius: 8px; margin: 20px 0; }
        .footer { background-color: #f8fafc; padding: 20px; text-align: center; font-size: 12px; color: #64748b; }
      </style>
    </head>
    <body>
      <div class="container">
        <div class="card">
          <div class="header">
            <h1>📊 Financial Report</h1>
          </div>
          <div class="content">
            <h2 style="color: #1e3a5f; margin-top: 0;">${escapeHtml(companyName)} - ${escapeHtml(reportName)}</h2>
            <div class="report-details">
              <p style="margin: 5px 0;"><strong>💰 Total Income:</strong> $${totalIncome.toLocaleString()}</p>
              <p style="margin: 5px 0;"><strong>📉 Total Expenses:</strong> $${totalExpenses.toLocaleString()}</p>
              <p style="margin: 5px 0;" class="${profit >= 0 ? 'profit' : 'loss'}"><strong>📈 Net Profit:</strong> $${profit.toLocaleString()}</p>
            </div>
            <div style="text-align: center;">
              <a href="https://estatesflow.online/accounting" class="button">View Full Report</a>
            </div>
          </div>
          <div class="footer">
            <p>${escapeHtml(companyName)} - Powered by EstatesFlow CRM</p>
            <p>📍 Jikwoyi, AMAC Abuja, Nigeria</p>
          </div>
        </div>
      </div>
    </body>
    </html>
  `

  return sendEmail({
    to,
    subject: `Financial Report - ${reportName} - ${companyName}`,
    html,
    fromName: companyName
  })
}

// ============== INTERVIEW INVITATION (Recruitment Portal) ==============
export const sendInterviewInvitation = async (to, candidateName, jobTitle, interviewDate, interviewTime, interviewLocation, companyName) => {
  const html = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>Interview Invitation</title>
      <style>
        body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; margin: 0; padding: 0; background-color: #f4f4f4; }
        .container { max-width: 600px; margin: 0 auto; padding: 20px; }
        .card { background: #ffffff; border-radius: 8px; overflow: hidden; box-shadow: 0 2px 10px rgba(0,0,0,0.1); }
        .header { background: linear-gradient(135deg, #1e3a5f 0%, #0f2b42 100%); padding: 30px; text-align: center; }
        .header h1 { color: #ffffff; margin: 0; font-size: 24px; }
        .content { padding: 30px; }
        .interview-details { background-color: #f0f4f8; padding: 20px; border-radius: 8px; margin: 20px 0; }
        .button { display: inline-block; background-color: #059669; color: #ffffff; padding: 12px 24px; text-decoration: none; border-radius: 8px; margin: 20px 0; }
        .footer { background-color: #f8fafc; padding: 20px; text-align: center; font-size: 12px; color: #64748b; }
      </style>
    </head>
    <body>
      <div class="container">
        <div class="card">
          <div class="header">
            <h1>🎯 Interview Invitation</h1>
          </div>
          <div class="content">
            <h2 style="color: #1e3a5f; margin-top: 0;">Dear ${escapeHtml(candidateName)},</h2>
            <p>Congratulations! Your application for <strong>${escapeHtml(jobTitle)}</strong> at <strong>${escapeHtml(companyName)}</strong> has been shortlisted.</p>
            <div class="interview-details">
              <p style="margin: 5px 0;"><strong>📅 Date:</strong> ${interviewDate}</p>
              <p style="margin: 5px 0;"><strong>⏰ Time:</strong> ${interviewTime}</p>
              <p style="margin: 5px 0;"><strong>📍 Location:</strong> ${escapeHtml(interviewLocation)}</p>
            </div>
            <p>Please confirm your availability by replying to this email.</p>
            <div style="text-align: center;">
              <a href="mailto:${companyName.replace(/\s/g, '')}@estatesflow.com" class="button">Confirm Availability</a>
            </div>
          </div>
          <div class="footer">
            <p>${escapeHtml(companyName)} Recruitment Team</p>
            <p>📍 Jikwoyi, AMAC Abuja, Nigeria</p>
          </div>
        </div>
      </div>
    </body>
    </html>
  `

  return sendEmail({
    to,
    subject: `Interview Invitation: ${jobTitle} - ${companyName}`,
    html,
    fromName: companyName
  })
}

// ============== CONTRACT REMINDER (Legal Portal) ==============
export const sendContractReminderEmail = async (to, clientName, contractTitle, expiryDate, companyName) => {
  const html = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>Contract Reminder</title>
      <style>
        body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; margin: 0; padding: 0; background-color: #f4f4f4; }
        .container { max-width: 600px; margin: 0 auto; padding: 20px; }
        .card { background: #ffffff; border-radius: 8px; overflow: hidden; box-shadow: 0 2px 10px rgba(0,0,0,0.1); }
        .header { background: linear-gradient(135deg, #1e3a5f 0%, #0f2b42 100%); padding: 30px; text-align: center; }
        .header h1 { color: #ffffff; margin: 0; font-size: 24px; }
        .content { padding: 30px; }
        .contract-details { background-color: #fef3c7; padding: 20px; border-radius: 8px; margin: 20px 0; border-left: 4px solid #f59e0b; }
        .footer { background-color: #f8fafc; padding: 20px; text-align: center; font-size: 12px; color: #64748b; }
      </style>
    </head>
    <body>
      <div class="container">
        <div class="card">
          <div class="header">
            <h1>📋 Contract Reminder</h1>
          </div>
          <div class="content">
            <h2 style="color: #1e3a5f; margin-top: 0;">Dear ${escapeHtml(clientName)},</h2>
            <p>Your contract <strong>${escapeHtml(contractTitle)}</strong> with <strong>${escapeHtml(companyName)}</strong> will expire on <strong>${new Date(expiryDate).toLocaleDateString()}</strong>.</p>
            <div class="contract-details">
              <p style="margin: 0; color: #92400e;">⚠️ Please contact us to discuss renewal options.</p>
            </div>
            <p>Don't let your contract expire without review. Contact us today!</p>
          </div>
          <div class="footer">
            <p>${escapeHtml(companyName)} - Legal Department</p>
            <p>📍 Jikwoyi, AMAC Abuja, Nigeria</p>
          </div>
        </div>
      </div>
    </body>
    </html>
  `

  return sendEmail({
    to,
    subject: `Contract Reminder: ${contractTitle} - ${companyName}`,
    html,
    fromName: companyName
  })
}

// ============== PASSWORD RESET (Login) - FIXED ==============
export const sendPasswordResetEmail = async (to, resetLink, companyName = 'EstatesFlow') => {
  // Ensure the reset link uses the correct domain
  let fullResetLink = resetLink || `${window.location.origin}/reset-password`
  
  // If the reset link doesn't have the hash format, construct it properly
  if (resetLink && !resetLink.includes('#access_token')) {
    // Try to extract token from the link
    const tokenMatch = resetLink.match(/[?&]token=([^&]+)/)
    if (tokenMatch) {
      fullResetLink = `https://estatesflow.online/reset-password#access_token=${tokenMatch[1]}&type=recovery`
    } else {
      fullResetLink = `https://estatesflow.online/reset-password`
    }
  } else if (resetLink && resetLink.includes('#access_token')) {
    // If it already has the hash format, just make sure it uses the correct domain
    const hashMatch = resetLink.match(/#access_token=([^&]+)/)
    if (hashMatch) {
      fullResetLink = `https://estatesflow.online/reset-password#access_token=${hashMatch[1]}&type=recovery`
    }
  }

  console.log('Sending reset link:', fullResetLink)

  const html = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>Reset Your Password</title>
      <style>
        body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; margin: 0; padding: 0; background-color: #f4f4f4; }
        .container { max-width: 600px; margin: 0 auto; padding: 20px; }
        .card { background: #ffffff; border-radius: 8px; overflow: hidden; box-shadow: 0 2px 10px rgba(0,0,0,0.1); }
        .header { background: linear-gradient(135deg, #1e3a5f 0%, #0f2b42 100%); padding: 30px; text-align: center; }
        .header h1 { color: #ffffff; margin: 0; font-size: 24px; }
        .content { padding: 30px; }
        .button { display: inline-block; background-color: #1e3a5f; color: #ffffff; padding: 12px 24px; text-decoration: none; border-radius: 8px; margin: 20px 0; }
        .warning { background-color: #fef3c7; padding: 15px; border-radius: 8px; margin: 20px 0; border-left: 4px solid #f59e0b; }
        .footer { background-color: #f8fafc; padding: 20px; text-align: center; font-size: 12px; color: #64748b; }
      </style>
    </head>
    <body>
      <div class="container">
        <div class="card">
          <div class="header">
            <h1>🔐 Reset Your Password</h1>
          </div>
          <div class="content">
            <h2 style="color: #1e3a5f; margin-top: 0;">Password Reset Request</h2>
            <p>We received a request to reset your password for your ${escapeHtml(companyName)} account.</p>
            
            <div class="warning">
              <p style="margin: 0; color: #92400e;">⚠️ This link will expire in <strong>1 hour</strong></p>
            </div>
            
            <p>Click the button below to create a new password:</p>
            
            <div style="text-align: center;">
              <a href="${fullResetLink}" class="button" style="background-color: #1e3a5f; color: #ffffff; text-decoration: none; padding: 12px 24px; border-radius: 8px; display: inline-block;">Reset Password</a>
            </div>
            
            <p style="font-size: 12px; color: #64748b; text-align: center; word-break: break-all;">Or copy this link: ${fullResetLink}</p>
            
            <hr style="margin: 20px 0;" />
            
            <p style="font-size: 12px; color: #64748b;">If you didn't request this password reset, please ignore this email. Your password will remain unchanged.</p>
            <p style="font-size: 12px; color: #64748b;">For security, this link can only be used once and expires after 1 hour.</p>
          </div>
          <div class="footer">
            <p>${escapeHtml(companyName)} - Complete Real Estate Management</p>
            <p>📍 Jikwoyi, AMAC Abuja, Nigeria</p>
            <p>🔒 This is an automated message, please do not reply</p>
          </div>
        </div>
      </div>
    </body>
    </html>
  `

  return sendEmail({
    to,
    subject: `Reset Your ${companyName} Password`,
    html,
    fromName: companyName
  })
}


// Generate a secure random token for password reset
export const generateResetToken = () => {
  return crypto.randomBytes(32).toString('hex')
}

// Store reset token in database (you need to create a password_resets table)
export const storeResetToken = async (email, token) => {
  const { error } = await supabase
    .from('password_resets')
    .insert({
      email: email,
      token: token,
      expires_at: new Date(Date.now() + 60 * 60 * 1000) // 1 hour
    })
  
  return !error
}      

// ============== WELCOME EMAIL (Onboarding) ==============
export const sendWelcomeEmail = async (to, firstName, companyName) => {
  const loginLink = 'https://estatesflow.online/login'
  const html = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>Welcome to EstatesFlow CRM</title>
      <style>
        body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; margin: 0; padding: 0; background-color: #f4f4f4; }
        .container { max-width: 600px; margin: 0 auto; padding: 20px; }
        .card { background: #ffffff; border-radius: 8px; overflow: hidden; box-shadow: 0 2px 10px rgba(0,0,0,0.1); }
        .header { background: linear-gradient(135deg, #1e3a5f 0%, #0f2b42 100%); padding: 30px; text-align: center; }
        .header h1 { color: #ffffff; margin: 0; font-size: 24px; }
        .content { padding: 30px; }
        .next-steps { background-color: #f0f4f8; padding: 20px; border-radius: 8px; margin: 20px 0; }
        .button { display: inline-block; background-color: #1e3a5f; color: #ffffff; padding: 12px 24px; text-decoration: none; border-radius: 8px; margin: 20px 0; }
        .footer { background-color: #f8fafc; padding: 20px; text-align: center; font-size: 12px; color: #64748b; }
      </style>
    </head>
    <body>
      <div class="container">
        <div class="card">
          <div class="header">
            <h1>🏢 Welcome to ${escapeHtml(companyName)}</h1>
          </div>
          <div class="content">
            <h2 style="color: #1e3a5f; margin-top: 0;">Welcome, ${escapeHtml(firstName)}! 🎉</h2>
            <p>We're excited to have you on board. You now have access to EstatesFlow CRM's complete real estate management platform.</p>
            <div class="next-steps">
              <p><strong>✅ Next steps:</strong></p>
              <ul>
                <li>Complete your profile setup</li>
                <li>Add your first property</li>
                <li>Invite team members</li>
                <li>Explore all portals</li>
              </ul>
            </div>
            <div style="text-align: center;">
              <a href="https://estatesflow.online/Leadership" class="button">Go to Dashboard</a>
            </div>
          </div>
          <div class="footer">
            <p>Need help? Contact support@estatesflow.com</p>
            <p>📍 Jikwoyi, AMAC Abuja, Nigeria</p>
          </div>
        </div>
      </div>
    </body>
    </html>
  `

  return sendEmail({
    to,
    subject: `Welcome to EstatesFlow CRM, ${firstName}!`,
    html,
    fromName: companyName
  })
}

// ============== HELPER FUNCTION ==============
function escapeHtml(str) {
  if (!str) return ''
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}